import { Typography } from '@healingpaper/cell'
import { IcArrowDown, IcCross } from '@healingpaper/cell-icons'
import Cookies from 'js-cookie'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getEnv } from '@unni/platform'

import { DistrictResponseController, useDistrictReader } from '@/features/district-filter/api/get-districts'
import { postAmplitude } from '@/core/lib'

import type { Locales } from '@/utils/language'
import type { ReactNode } from 'react'

interface ServiceOfferSearchFilterProps {
  sortingComponent?: ReactNode
}

export const ServiceOfferSearchFilter = ({ sortingComponent }: ServiceOfferSearchFilterProps) => {
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const { data: districtResponse } = useDistrictReader(
    {
      Authorization: Cookies.get('token') || '',
      'Accept-Language': i18n.language as Locales,
    },
    i18n.language as Locales,
  )

  const districtFilterUrl: string = useMemo(() => {
    const { district, category, q } = router.query
    const keyword = (category as string) || (q as string)

    const params = new URLSearchParams()
    if (keyword) {
      params.set('q', keyword)
    }
    if (district) {
      params.set('district', district as string)
    }

    const queryString = params.toString()
    return `/district-filter${queryString ? `?${queryString}` : ''}`
  }, [i18n.language, router.query])

  const selectedDistrictName: string | undefined = useMemo(() => {
    const districtResponseController = new DistrictResponseController(districtResponse)
    const selectedDistrict = districtResponseController.getByCode(router.query.district as string)
    return selectedDistrict?.name
  }, [districtResponse, router.query.district])
  const hasSelectedDistrict: boolean = selectedDistrictName !== undefined

  return (
    <section className="flex flex-row items-center py-3 pl-5 gap-4">
      <span className="flex flex-row items-center py-[6px]">
        <Typography type="title4" elementType="h2">
          {t('pages.eventList.filter.title')}
        </Typography>
      </span>
      <div className="flex flex-row items-center gap-[2px]">
        <Link
          href={districtFilterUrl}
          className={`flex flex-row items-center gap-1 px-3 py-2 border-[1px] border-[#D6D6D6] rounded-[8px] ${selectedDistrictName ? 'bg-[#FFF7E0] border-[#FF540F]' : ''}`}
          onClick={() => {
            postAmplitude('click_search_filter', {
              type: 'district',
            })
          }}
        >
          <Typography
            type="caption1"
            elementType="span"
            style={{
              color: hasSelectedDistrict ? '#FF540F' : '#666666',
            }}
          >
            {selectedDistrictName || t('pages.eventList.districtFilter.defaultText')}
          </Typography>
          <IcArrowDown width={16} height={16} color={hasSelectedDistrict ? '#FF540F' : '#666666'} />
        </Link>

        {hasSelectedDistrict && (
          <Link
            href={(() => {
              const currentUrl = new URL(window.location.href)
              currentUrl.searchParams.delete('district')
              return currentUrl.toString()
            })()}
            className="flex flex-row items-center p-2"
            onClick={() => {
              postAmplitude('click_search_filter', {
                type: 'district',
              })
            }}
          >
            <IcCross width={12} height={12} color="#666666" />
          </Link>
        )}
      </div>

      {sortingComponent && <div className="flex flex-row items-center">{sortingComponent}</div>}
    </section>
  )
}
